import Vue from "vue";
import VueRouter from "vue-router";
import AuthLayout from "@/layouts/Auth";
import Login from "@/views/auth/Login";
import ResetPassword from "@/views/auth/ResetPassword";

import Register from "@/views/auth/Register";
import CompleteRegister from "@/views/auth/CompleteRegister";
import Join from "@/views/auth/Join";
import EmailVerify from "@/views/auth/EmailVerify";
import Autologin from "@/views/auth/Autologin";
import ForgotPassword from "@/views/auth/ForgotPassword";

import i18n from "@/i18n";
import store from "@/store";
import { USER_STATUSES } from "@/constant";
import Redirect from "@/views/auth/Redirect";
import ZitadelLoginCallback from "@/views/auth/ZitadelLoginCallback";

Vue.use(VueRouter);

const routes = [
  {
    path: "",
    redirect: "login",
    component: AuthLayout,
    children: [
      {
        path: "/login",
        name: "login",
        component: Login,
        meta: {
          auth: false,
          title: i18n.t("routes.login"),
          layout: "blank",
          onlyLightTheme: true,
        },
      },
      ...(() => {
        if (process.env.VUE_APP_INULTI_BUILD !== "true") {
          return [
            {
              path: "/register",
              name: "register",
              component: Register,
              props: route => ({ userData: route.params.userData }),
              meta: {
                auth: false,
                title: i18n.t("routes.register"),
                layout: "blank",
                onlyLightTheme: true,
              },
            },
            {
              path: "/complete-register",
              name: "complete-register",
              component: CompleteRegister,
              meta: {
                authenticated: true,
                title: i18n.t("routes.complete_register"),
                layout: "blank",
                onlyLightTheme: true,
              },
            },
          ];
        } else {
          return [];
        }
      })(),
      // Organization invite
      {
        path: "/join/:token",
        name: "join",
        component: Join,
        meta: {
          auth: false,
          priority: true,
          title: i18n.t("routes.join"),
          layout: "blank",
          onlyLightTheme: true,
        },
      },
      // Email verification route
      {
        path: "/email-verify/:token",
        name: "email-verify",
        component: EmailVerify,
        meta: {
          auth: false,
          /**
           * TODO: Rename to something like "skipAuthCheck".
           * Regarding the naming, "priority" has nothing to do with the actual functionality of this property -
           * when set to true, route is accessible to both authenticated and not authenticated users...
           */
          priority: true,
          title: i18n.t("routes.email_verify"),
          layout: "blank",
          onlyLightTheme: true,
        },
      },
      {
        path: "/autologin",
        name: "autologin",
        component: Autologin,
        meta: {
          auth: false,
          title: i18n.t("routes.autologin"),
          layout: "blank",
          onlyLightTheme: true,
        },
      },
      {
        path: "/forgot-password",
        name: "forgot_password",
        component: ForgotPassword,
        meta: {
          auth: false,
          title: i18n.t("routes.forgot_password"),
          layout: "blank",
          onlyLightTheme: true,
        },
      },
      {
        path: "/reset-password/:token",
        name: "reset_password",
        component: ResetPassword,
        props: true,
        meta: {
          priority: true,
          auth: false,
          title: i18n.t("routes.reset_password"),
          layout: "blank",
          onlyLightTheme: true,
        },
      },
    ],
  },
  {
    path: "/redirect",
    name: "redirect",
    component: Redirect,
    props: true,
    meta: {
      auth: false,
      title: i18n.t("routes.redirecting"),
      layout: "blank",
      onlyLightTheme: true,
    },
  },
  // Zitadel - Microsoft login callback modal window
  {
    path: "/zitadel-callback",
    name: "zitadel-callback",
    component: ZitadelLoginCallback,
    props: true,
    meta: {
      auth: false,
      title: i18n.t("routes.redirecting"),
      layout: "blank",
      onlyLightTheme: true,
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL || "/",
  routes,
});

router.beforeEach(async (to, from, next) => {
  //save previous route
  store.commit("SET_PREVIOUS_ROUTE", from.fullPath);
  // Checker for browser back button and navigation buttons
  if (from.meta.disableRedirect) {
    window.history.pushState({}, "", to.fullPath);

    return next({ path: from.fullPath });
  }

  // For all the routes that need authentication (Dashboard routes after login)
  if (to.meta?.auth !== false) {
    const currentUser = store.getters.getCurrentUser;

    // If user is partially registered
    if (currentUser.status === USER_STATUSES.PARTIALLY_REGISTERED && to.name !== "complete-register") {
      return next({ name: "complete-register" });
    }

    try {
      next();
    } catch (error) {
      // Handle the error if the API call fails
      console.error(error);

      // Call the next() function to proceed with the route transition anyway
      next();
    }
  } else {
    next();
  }
});

export default router;
